import { useState, useMemo, useCallback } from "react";
import { motion, AnimatePresence } from "motion/react";
import Navbar from "@/components/feature/Navbar";
import Footer from "@/components/feature/Footer";
import { materials as mockMaterials, materialCategories } from "@/mocks/materials";
import { useMaterials, Material } from "@/hooks/useMaterials";
import AdminUpload from "./components/AdminUpload";

const containerVariants = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: { staggerChildren: 0.06 },
  },
};

const itemVariants = {
  hidden: { opacity: 0, y: 20 },
  visible: { opacity: 1, y: 0 },
};

export default function MaterialsPage() {
  const [activeCategory, setActiveCategory] = useState("all");
  const [searchQuery, setSearchQuery] = useState("");
  const [toastMessage, setToastMessage] = useState<string | null>(null);

  // Admin mode
  const [adminMode, setAdminMode] = useState(false);
  const [adminPassword, setAdminPassword] = useState("");
  const [adminError, setAdminError] = useState("");
  const [showAdminLogin, setShowAdminLogin] = useState(false);
  const [uploadOpen, setUploadOpen] = useState(false);

  // Supabase data
  const { materials: dbMaterials, loading, error: dbError, uploadMaterial, deleteMaterial, incrementDownload } = useMaterials();

  // Use DB data if available, otherwise fall back to mock data
  const allMaterials: (Material | typeof mockMaterials[0])[] = useMemo(() => {
    if (dbMaterials.length > 0) return dbMaterials;
    return mockMaterials;
  }, [dbMaterials]);

  const filtered = useMemo(() => {
    let items = [...allMaterials];
    if (activeCategory !== "all") {
      items = items.filter((m) => m.category === activeCategory);
    }
    if (searchQuery) {
      const q = searchQuery.toLowerCase();
      items = items.filter(
        (m) =>
          m.title.toLowerCase().includes(q) ||
          m.description.toLowerCase().includes(q) ||
          m.category_label.toLowerCase().includes(q)
      );
    }
    return items;
  }, [allMaterials, activeCategory, searchQuery]);

  const showToast = (msg: string) => {
    setToastMessage(msg);
    setTimeout(() => setToastMessage(null), 2800);
  };

  // Supabase 행(file_url)과 목업(downloadUrl 을 쓰던 옛 형태)을 함께 받는다.
  // `"key" in m` 내로잉은 선언에 없는 키를 unknown 으로 만들어 버리므로
  // 레코드로 한 번 내려서 값 타입을 직접 확인한다.
  const getMaterialFileUrl = (m: Material | typeof mockMaterials[0]): string | undefined => {
    const rec = m as Record<string, unknown>;
    if (typeof rec.file_url === "string" && rec.file_url) return rec.file_url;
    if (typeof rec.downloadUrl === "string" && rec.downloadUrl) return rec.downloadUrl;
    return undefined;
  };

  const getMaterialId = (m: Material | typeof mockMaterials[0]): number | string => m.id;

  const handleDownload = (material: Material | typeof mockMaterials[0]) => {
    const url = getMaterialFileUrl(material);
    if (url) {
      const link = document.createElement("a");
      link.href = url;
      link.download = material.title;
      link.target = "_blank";
      link.rel = "noopener noreferrer";
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);

      // Increment download count if it's a DB material
      const id = getMaterialId(material);
      if (typeof id === "number" && dbMaterials.length > 0) {
        incrementDownload(id);
      }
    }
    showToast(`${material.title} 다운로드가 시작되었습니다`);
  };

  const handleAdminToggle = () => {
    if (adminMode) {
      setAdminMode(false);
      setAdminPassword("");
      setAdminError("");
    } else {
      setShowAdminLogin(true);
    }
  };

  const handleAdminLogin = () => {
    if (adminPassword === "admin1234") {
      setAdminMode(true);
      setShowAdminLogin(false);
      setAdminPassword("");
      setAdminError("");
    } else {
      setAdminError("비밀번호가 일치하지 않습니다");
    }
  };

  const handleUpload = useCallback(async (
    file: File,
    metadata: { title: string; description: string; category: string; category_label: string }
  ) => {
    await uploadMaterial(file, metadata);
  }, [uploadMaterial]);

  const handleDelete = async (material: Material | typeof mockMaterials[0]) => {
    const id = getMaterialId(material);
    if (typeof id !== "number") {
      showToast("목 데이터는 삭제할 수 없습니다");
      return;
    }
    try {
      await deleteMaterial(id);
      showToast("자료가 삭제되었습니다");
    } catch (e) {
      showToast(e instanceof Error ? e.message : "삭제 실패");
    }
  };

  return (
    <div className="min-h-screen bg-background-50 font-['Noto_Sans_KR']">
      <Navbar />
      <main className="pt-24 pb-16 px-4 md:px-6">
        <div className="max-w-[1280px] mx-auto">
          {/* Header with admin toggle */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, ease: [0.25, 0.1, 0.25, 1] as const }}
            className="mb-8 flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4"
          >
            <div>
              <div className="flex items-center gap-2 mb-2">
                <i className="ri-folder-download-line text-[#C4956A]"></i>
                <span className="text-xs font-medium text-[#C4956A]">강의자료실</span>
              </div>
              <h1 className="text-2xl md:text-3xl font-bold text-foreground-950 mb-2">
                강의자료실
              </h1>
              <p className="text-sm text-foreground-600">
                강의에서 사용된 PPT, PDF 자료들을 자유롭게 다운로드하세요
              </p>
            </div>

            {/* Admin Controls */}
            <div className="flex items-center gap-2">
              {adminMode && (
                <button
                  onClick={() => setUploadOpen(true)}
                  className="inline-flex items-center gap-2 px-4 py-2 text-sm font-semibold rounded-full bg-[#C4956A] text-white hover:bg-[#B08050] transition-all whitespace-nowrap cursor-pointer"
                >
                  <i className="ri-add-line"></i>
                  자료 등록
                </button>
              )}
              <button
                onClick={handleAdminToggle}
                className={`inline-flex items-center gap-1.5 px-3 py-2 text-xs font-medium rounded-full transition-all whitespace-nowrap cursor-pointer ${
                  adminMode
                    ? "bg-red-50 text-red-500 border border-red-200 hover:bg-red-100"
                    : "bg-white border border-[#E8E5E0] text-[#B8B3AE] hover:border-[#C4956A] hover:text-[#C4956A]"
                }`}
              >
                <i className={`text-xs ${adminMode ? "ri-shield-check-line" : "ri-shield-keyhole-line"}`}></i>
                {adminMode ? "관리자 모드 종료" : "관리자"}
              </button>
            </div>
          </motion.div>

          {/* Admin Login Modal */}
          <AnimatePresence>
            {showAdminLogin && !adminMode && (
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4"
                onClick={() => { setShowAdminLogin(false); setAdminError(""); setAdminPassword(""); }}
              >
                <motion.div
                  initial={{ opacity: 0, scale: 0.95, y: 20 }}
                  animate={{ opacity: 1, scale: 1, y: 0 }}
                  exit={{ opacity: 0, scale: 0.95, y: 20 }}
                  transition={{ duration: 0.25, ease: "easeOut" as const }}
                  onClick={(e) => e.stopPropagation()}
                  className="bg-white rounded-2xl w-full max-w-[380px] p-6"
                >
                  <div className="text-center mb-5">
                    <div className="w-12 h-12 mx-auto mb-3 rounded-full bg-[#C4956A]/10 flex items-center justify-center">
                      <i className="ri-shield-keyhole-line text-xl text-[#C4956A]"></i>
                    </div>
                    <h3 className="text-base font-bold text-[#1C1917] mb-1">관리자 인증</h3>
                    <p className="text-xs text-[#8C8780]">자료 등록/삭제를 위해 비밀번호를 입력하세요</p>
                  </div>
                  <input
                    type="password"
                    value={adminPassword}
                    onChange={(e) => { setAdminPassword(e.target.value); setAdminError(""); }}
                    onKeyDown={(e) => { if (e.key === "Enter") handleAdminLogin(); }}
                    placeholder="비밀번호 입력"
                    className="w-full px-4 py-2.5 text-sm border border-[#E8E5E0] rounded-lg bg-white focus:outline-none focus:border-[#C4956A] transition-colors mb-3"
                    autoFocus
                  />
                  {adminError && (
                    <p className="text-xs text-red-500 mb-3">{adminError}</p>
                  )}
                  <div className="flex gap-2">
                    <button
                      onClick={() => { setShowAdminLogin(false); setAdminError(""); setAdminPassword(""); }}
                      className="flex-1 py-2.5 text-sm font-medium border border-[#E8E5E0] rounded-full text-[#5C5650] hover:bg-[#F5F0EC] transition-all whitespace-nowrap"
                    >
                      취소
                    </button>
                    <button
                      onClick={handleAdminLogin}
                      className="flex-1 py-2.5 text-sm font-semibold bg-[#1C1917] text-white rounded-full hover:bg-[#C4956A] transition-all whitespace-nowrap"
                    >
                      확인
                    </button>
                  </div>
                </motion.div>
              </motion.div>
            )}
          </AnimatePresence>

          {/* Search */}
          <motion.div
            initial={{ opacity: 0, y: 15 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1, duration: 0.4, ease: [0.25, 0.1, 0.25, 1] as const }}
            className="relative mb-6 max-w-md"
          >
            <i className="ri-search-line absolute left-3 top-1/2 -translate-y-1/2 text-sm text-foreground-500"></i>
            <input
              type="text"
              placeholder="자료 제목, 내용, 카테고리 검색"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full pl-10 pr-4 py-2.5 text-sm border border-[#E8E5E0] rounded-lg bg-white focus:outline-none focus:border-[#C4956A] transition-colors"
            />
          </motion.div>

          {/* Category Tabs */}
          <motion.div
            initial={{ opacity: 0, y: 15 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.15, duration: 0.4, ease: [0.25, 0.1, 0.25, 1] as const }}
            className="flex items-center gap-2 mb-8 overflow-x-auto pb-2"
          >
            {materialCategories.map((cat) => (
              <button
                key={cat.key}
                onClick={() => setActiveCategory(cat.key)}
                className={`px-4 py-2 text-sm rounded-full transition-all whitespace-nowrap ${
                  activeCategory === cat.key
                    ? "bg-[#1C1917] text-white font-medium"
                    : "bg-white border border-[#E8E5E0] text-foreground-700 hover:border-[#C4956A] hover:text-[#C4956A]"
                }`}
              >
                {cat.label}
              </button>
            ))}
          </motion.div>

          {/* Status bar */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.2 }}
            className="flex items-center justify-between mb-5"
          >
            <p className="text-xs text-foreground-500">
              총 <strong className="text-foreground-950">{filtered.length}</strong>개의 자료
              {dbMaterials.length > 0 && (
                <span className="ml-1 text-[#C4956A]">(DB 연동)</span>
              )}
            </p>
            {loading && (
              <span className="text-xs text-[#8C8780] flex items-center gap-1">
                <i className="ri-loader-4-line animate-spin"></i>
                불러오는 중...
              </span>
            )}
          </motion.div>

          {/* DB Error */}
          {dbError && !loading && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              className="mb-5 p-3 bg-amber-50 border border-amber-200 rounded-lg text-xs text-amber-700 flex items-center gap-2"
            >
              <i className="ri-information-line"></i>
              DB 연결 실패 - 목업 데이터를 표시합니다
            </motion.div>
          )}

          {/* Materials Grid */}
          <motion.div
            variants={containerVariants}
            initial="hidden"
            animate="visible"
            key={activeCategory + searchQuery}
            className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5"
          >
            {filtered.map((material, idx) => (
              <motion.div
                key={getMaterialId(material)}
                variants={itemVariants}
                className="group bg-white rounded-xl border border-[#E8E5E0] overflow-hidden hover:border-[#C4956A]/30 transition-all flex flex-col relative"
              >
                {/* Delete button - admin mode */}
                {adminMode && (
                  <button
                    onClick={() => handleDelete(material)}
                    className="absolute top-3 right-3 z-10 w-7 h-7 rounded-full bg-red-50 border border-red-200 text-red-400 hover:bg-red-100 hover:text-red-500 flex items-center justify-center transition-all cursor-pointer"
                    title="자료 삭제"
                  >
                    <i className="ri-delete-bin-line text-xs"></i>
                  </button>
                )}

                {/* Thumbnail Area */}
                <div className="relative aspect-[4/3] bg-[#FAF8F6] flex items-center justify-center overflow-hidden">
                  <div className="absolute inset-0 opacity-[0.04] pointer-events-none"
                    style={{
                      backgroundImage: `radial-gradient(circle at 20% 30%, #C4956A 1px, transparent 1px), radial-gradient(circle at 80% 70%, #C4956A 1px, transparent 1px)`,
                      backgroundSize: '20px 20px',
                    }}
                  />
                  <div className={`w-20 h-24 relative flex flex-col items-center justify-center rounded-lg transition-transform duration-500 group-hover:scale-110 ${
                    material.file_type === 'pdf'
                      ? 'bg-red-50 border-2 border-red-200'
                      : 'bg-orange-50 border-2 border-orange-200'
                  }`}>
                    <i className={`text-3xl mb-1 ${
                      material.file_type === 'pdf'
                        ? 'ri-file-pdf-line text-red-500'
                        : 'ri-file-ppt-line text-orange-500'
                    }`}></i>
                    <span className={`text-[10px] font-bold uppercase ${
                      material.file_type === 'pdf' ? 'text-red-400' : 'text-orange-400'
                    }`}>
                      {material.file_type}
                    </span>
                  </div>
                  <div className="absolute top-3 left-3">
                    <span className="px-2 py-0.5 text-xs font-medium bg-[#C4956A] text-white rounded">
                      {material.category_label}
                    </span>
                  </div>
                  <div className="absolute top-3 right-3">
                    <span className="px-2 py-0.5 text-xs font-medium bg-white/80 text-foreground-600 rounded border border-[#E8E5E0]">
                      {material.file_size}
                    </span>
                  </div>
                </div>

                {/* Content */}
                <div className="p-4 flex flex-col flex-1">
                  <h3 className="text-sm font-semibold text-foreground-950 mb-2 line-clamp-2 leading-snug group-hover:text-[#C4956A] transition-colors">
                    {material.title}
                  </h3>
                  <p className="text-xs text-foreground-500 mb-3 line-clamp-2 leading-relaxed flex-1">
                    {material.description}
                  </p>
                  <div className="flex items-center justify-between text-xs text-foreground-400 pt-3 border-t border-[#F0EDE8]">
                    <span className="flex items-center gap-1">
                      <i className="ri-calendar-line text-[#C4956A]"></i>
                      {material.updated_at.slice(0, 10)}
                    </span>
                    <span className="flex items-center gap-1">
                      <i className="ri-download-line"></i>
                      {(material.download_count ?? 0).toLocaleString()}
                    </span>
                  </div>
                  <button
                    onClick={() => handleDownload(material)}
                    className="mt-4 w-full flex items-center justify-center gap-2 py-2.5 text-sm font-semibold rounded-lg bg-[#C4956A] text-white hover:bg-[#B08050] transition-all whitespace-nowrap cursor-pointer"
                  >
                    <i className="ri-download-2-line"></i>
                    다운로드
                  </button>
                </div>
              </motion.div>
            ))}
          </motion.div>

          {/* Empty State */}
          {filtered.length === 0 && !loading && (
            <div className="text-center py-20">
              <div className="w-16 h-16 mx-auto mb-4 rounded-2xl bg-[#C4956A]/10 flex items-center justify-center">
                <i className="ri-file-search-line text-2xl text-[#C4956A]"></i>
              </div>
              <p className="text-foreground-700 font-medium mb-1">
                검색 결과가 없습니다
              </p>
              <p className="text-sm text-foreground-500">
                다른 카테고리나 검색어로 다시 시도해보세요
              </p>
            </div>
          )}

          {/* Loading State */}
          {loading && filtered.length === 0 && (
            <div className="text-center py-20">
              <i className="ri-loader-4-line animate-spin text-2xl text-[#C4956A]"></i>
              <p className="text-sm text-[#8C8780] mt-3">자료를 불러오는 중...</p>
            </div>
          )}

          {/* Bottom CTA */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5, ease: [0.25, 0.1, 0.25, 1] as const }}
            className="mt-16"
          >
            <div className="bg-[#1C1917] rounded-2xl p-10 md:p-14 flex flex-col md:flex-row items-center justify-between gap-8">
              <div>
                <h2 className="text-xl md:text-2xl font-bold text-white mb-2">
                  더 깊이 있는 학습을 원하시나요?
                </h2>
                <p className="text-sm text-white/60 max-w-[420px]">
                  체계적인 커리큘럼으로 설계된 동영상 강의와 교육과정을 통해
                  경매 투자의 모든 것을 마스터하세요
                </p>
              </div>
              <div className="flex flex-col sm:flex-row gap-3">
                <a
                  href="/lectures"
                  className="inline-flex items-center justify-center gap-2 px-6 py-3 bg-white text-[#1C1917] text-sm font-semibold rounded-full hover:bg-[#F5F0EC] transition-all whitespace-nowrap cursor-pointer"
                >
                  <i className="ri-play-circle-line"></i>
                  동영상 강의 보기
                </a>
                <a
                  href="/courses"
                  className="inline-flex items-center justify-center gap-2 px-6 py-3 bg-[#C4956A] text-white text-sm font-semibold rounded-full hover:bg-[#B08050] transition-all whitespace-nowrap cursor-pointer"
                >
                  <i className="ri-book-open-line"></i>
                  교육과정 보기
                </a>
              </div>
            </div>
          </motion.div>
        </div>
      </main>
      <Footer />

      {/* Admin Upload Modal */}
      <AdminUpload
        isOpen={uploadOpen}
        onClose={() => setUploadOpen(false)}
        onUpload={handleUpload}
      />

      {/* Toast Notification */}
      <AnimatePresence>
        {toastMessage && (
          <motion.div
            initial={{ opacity: 0, y: 40 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: 40 }}
            className="fixed bottom-6 left-1/2 -translate-x-1/2 z-50 flex items-center gap-2 px-5 py-3 bg-[#1C1917] text-white text-sm font-medium rounded-full shadow-lg"
          >
            <i className="ri-checkbox-circle-line text-[#C4956A]"></i>
            {toastMessage}
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}